(ns ui.story-publish.components
  "Reusable components from the Instagram story publish panel."
  (:require [clojure.string :as str]
            [storrito.date.datetime-local :as datetime-local]))

(defn date-in-future?
  "Returns true if the date string represents a time in the future."
  [date-str]
  (datetime-local/date-in-future? date-str))

(defn current-datetime-local
  "Returns the current date/time formatted for a datetime-local input."
  []
  (datetime-local/current-datetime-local))

(defn preview-section
  [result]
  (let [preview-url (:story-media/preview-url result)
        video-url (:story-media/video-url result)
        media-type (:story-media/type result)]
    [:div
     {:class ["w-full" "sm:w-48" "md:w-56" "shrink-0"]}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900"
               "shadow-lg"]
       :style {:aspect-ratio "9/16"}}
      (cond
        (and video-url (#{:type.instagram/video :type.instagram/animation} media-type))
        [:video
         {:src video-url
          :poster preview-url
          :controls true
          :class ["w-full" "h-full" "object-cover"]}]

        preview-url
        [:img
         {:src preview-url
          :class ["w-full" "h-full" "object-cover"]}]

        :else
        [:div
         {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                  "text-gray-500"]}
         "No preview available"])]]))

(defn toggle-account
  [selected user]
  (let [user-id (:instagram.user/id user)]
    (if (some #(= (:instagram.user/id %) user-id) selected)
      (vec (remove #(= (:instagram.user/id %) user-id) selected))
      (conj (vec selected)
            {:instagram.user/id user-id
             :instagram.user/name (:instagram.user/name user)
             :share-to-fb? false}))))

(defn user-id->name
  "Resolves an Instagram user ID to its username via the users list."
  [users user-id]
  (or (some (fn [u]
              (when (= (:instagram.user/id u) user-id)
                (:instagram.user/name u)))
            users)
      user-id))

(defn checkmark-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "white" :class "w-3 h-3"}
   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
           :d "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"}]])

(defn dropdown-chevron-icon
  [open?]
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "currentColor"
         :class ["w-4" "h-4" "text-gray-400" "transition-transform"
                 (when open? "rotate-180")]}
   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
           :d "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"}]])

(defn account-selector
  [{:keys [state users selected-key dropdown-open-key title save-selection?
           on-change-actions]}]
  (let [selected (or (get state selected-key) [])
        dropdown-open (get state dropdown-open-key)]
    [:div
     {:class ["space-y-3"]}
     [:h3
      {:class ["text-sm" "font-semibold" "text-gray-700"]}
      (or title "Instagram Accounts")]
     (if (seq users)
       (let [all-disconnected (every? :instagram.account/not-connected users)]
         [:div
          {:class ["space-y-2"]}
          (when all-disconnected
            [:div
             {:class ["flex" "items-center" "gap-3" "px-4" "py-3" "rounded-lg"
                      "bg-amber-50" "border" "border-amber-200" "mb-2"]}
             [:div
              {:class ["flex-1" "min-w-0"]}
              [:p
               {:class ["text-sm" "font-medium" "text-amber-800"]}
               "All accounts are disconnected."]
              [:p
               {:class ["text-xs" "text-amber-600" "mt-0.5"]}
               "Reconnect at least one account to publish."]]
             [:a
              {:href "/ui/instagram-connect"
               :class ["shrink-0" "inline-flex" "items-center" "px-3" "py-1.5"
                       "text-xs" "font-medium" "text-amber-800" "bg-amber-100"
                       "hover:bg-amber-200" "rounded-md" "transition-colors"
                       "border" "border-amber-300"]}
              "Reconnect"]])
          [:div
           {:class ["relative"]}
           [:button
            {:class ["w-full" "flex" "items-center" "justify-between"
                     "px-3" "py-2.5" "text-sm" "border" "border-gray-300"
                     "rounded-lg" "bg-white" "hover:bg-gray-50"
                     "cursor-pointer" "transition-colors"]
             :on {:click [[:store/assoc dropdown-open-key (not dropdown-open)]]}}
            [:div
             {:class ["flex" "items-center" "gap-2" "flex-1" "min-w-0"]}
             (if (seq selected)
               [:div
                {:class ["flex" "items-center" "gap-1.5" "flex-wrap"]}
                (map
                  (fn [entry]
                    (let [user-id (:instagram.user/id entry)
                          user (some #(when (= (:instagram.user/id %) user-id) %)
                                     users)]
                      [:div
                       {:class ["flex" "items-center" "gap-1" "bg-indigo-50"
                                "border" "border-indigo-200" "rounded-full"
                                "pl-0.5" "pr-1" "py-0.5"]
                        :replicant/key user-id}
                       [:div
                        {:class ["w-5" "h-5" "rounded-full" "overflow-hidden"
                                 "bg-gray-200" "shrink-0"]}
                        (if (:instagram.user/profile-picture-url user)
                          [:img {:src (:instagram.user/profile-picture-url user)
                                 :class ["w-full" "h-full" "object-cover"]}]
                          [:div {:class ["w-full" "h-full" "flex" "items-center"
                                         "justify-center" "text-gray-400"
                                         "text-[9px]" "font-bold"]}
                           (some-> (:instagram.user/name user)
                                   first
                                   str
                                   str/upper-case)])]
                       [:span
                        {:class ["text-xs" "text-indigo-700" "truncate" "max-w-[100px]"]}
                        (:instagram.user/name user)]
                       [:span
                        {:class ["ml-0.5" "w-4" "h-4" "flex" "items-center"
                                 "justify-center" "rounded-full" "text-indigo-400"
                                 "hover:bg-indigo-200" "hover:text-indigo-700"
                                 "cursor-pointer" "leading-none" "text-sm"]
                         :title (str "Remove " (:instagram.user/name user))
                         :on {:click (vec
                                      (concat [[:js/stop-propagation]
                                               [:store/assoc selected-key
                                                (vec (remove #(= (:instagram.user/id %) user-id)
                                                             selected))]]
                                              on-change-actions))}}
                        "×"]]))
                  selected)]
               [:span
                {:class ["text-gray-400"]}
                "Select accounts..."])]
            (dropdown-chevron-icon dropdown-open)]
           (when dropdown-open
             [:div
              {:class ["fixed" "inset-0" "z-0"]
               :on {:click [[:store/assoc dropdown-open-key false]]}}])
           (when dropdown-open
             [:div
              {:class ["absolute" "z-10" "mt-1" "w-full" "bg-white" "border"
                       "border-gray-200" "rounded-lg" "shadow-lg"
                       "max-h-60" "overflow-y-auto"]}
              (map
                (fn [user]
                  (let [user-id (:instagram.user/id user)
                        is-selected (some #(= (:instagram.user/id %) user-id) selected)
                        not-connected (:instagram.account/not-connected user)]
                    [:div
                     {:class (into ["flex" "items-center" "gap-2.5" "px-3" "py-2"
                                    "cursor-pointer" "transition-colors"]
                                   (cond
                                     not-connected ["opacity-50" "cursor-default"]
                                     is-selected ["bg-indigo-50"]
                                     :else ["hover:bg-gray-50"]))
                      :replicant/key user-id
                      :on {:click (when-not not-connected
                                    (vec
                                      (concat (cond-> [[:store/assoc selected-key
                                                        (toggle-account selected user)]]
                                                (and save-selection? (not is-selected))
                                                (conj [:ig/save-selection user-id]))
                                              on-change-actions)))} }
                     [:div
                      {:class ["w-7" "h-7" "rounded-full" "overflow-hidden"
                               "bg-gray-200" "shrink-0"]}
                      (if (:instagram.user/profile-picture-url user)
                        [:img {:src (:instagram.user/profile-picture-url user)
                               :class ["w-full" "h-full" "object-cover"]}]
                        [:div {:class ["w-full" "h-full" "flex" "items-center"
                                       "justify-center" "text-gray-400"
                                       "text-xs" "font-bold"]}
                         (some-> (:instagram.user/name user)
                                 first
                                 str
                                 str/upper-case)])]
                     [:div
                      {:class ["flex-1" "min-w-0"]}
                      [:div
                       {:class ["text-sm" "text-gray-900" "truncate"]}
                       (:instagram.user/name user)]
                      (when not-connected
                        [:div
                         {:class ["text-xs" "text-red-500"]}
                         "Disconnected"])]
                     [:div
                      {:class (into ["w-4" "h-4" "rounded" "border-2" "flex"
                                     "items-center" "justify-center" "shrink-0"]
                                    (if is-selected
                                      ["border-indigo-500" "bg-indigo-500"]
                                      ["border-gray-300"]))}
                      (when is-selected
                        (checkmark-icon))]]))
                users)])]])
       [:div
        {:class ["text-sm" "text-gray-500" "p-4" "text-center"
                 "bg-gray-50" "rounded-lg" "border" "border-gray-200"]}
        "No Instagram accounts connected."])
     [:button
      {:class ["inline-flex" "items-center" "gap-1.5" "text-sm" "text-indigo-600"
               "hover:text-indigo-700" "font-medium" "cursor-pointer"]
       :on {:click [[:connect3-info/open {:source "story-publish"}]]}}
      "+ Add account"]]))

(defn auto-select-first-account
  "Returns initial selected-accounts with the previously used user
   pre-selected (persisted in localStorage). Falls back to the first
   connected user when no saved selection exists."
  [users saved-ig-user-id]
  (let [connected (remove :instagram.account/not-connected users)
        preferred (or (when saved-ig-user-id
                        (some #(when (= (:instagram.user/id %) saved-ig-user-id) %)
                              connected))
                      (first connected))]
    (when preferred
      [{:instagram.user/id (:instagram.user/id preferred)
        :instagram.user/name (:instagram.user/name preferred)
        :share-to-fb? false}])))

(defn future-credit-warning
  [result scheduled-label]
  [:div
   {:class ["p-3" "rounded-lg" "bg-blue-50" "border" "border-blue-200"]}
   [:p
    {:class ["text-sm" "text-blue-800"]}
    "Credits are consumed by usage over time. Scheduled " scheduled-label
    " only publish if your credit balance is positive at the scheduled time. "
    (when-let [balance (:credit-balance-formatted result)]
      [:span
       "Current balance: "
       [:span {:class ["font-medium"]} balance]
       ". "])
    [:a
     {:href "/ui/subscription?source=publish-flow"
      :class ["font-medium" "text-blue-900" "underline"]}
     "Add credits"]
    " if you plan to schedule far ahead."]])
